/* eslint-disable react/no-array-index-key */
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
// icons
import {
  ArrowRight, ExpandIcon,
  MoreIcon, RetractIcon, TrashIcon
} from '../Icons';
// comps
import { EmptyChart } from '../Charts';
import { IconButton } from '../IconButton';
import Selector from '../Selector/Simple';
import CardSettings from './CardSettings';
// hooks
import { usePromptConfirm } from '../../hooks';
// styles
import GraphFilter from 'src/sections/GroupCards/EvolutiveCrossChartCard/config/components/GraphFilter';
import ArrowRightDark from '../Icons/arrowRightDark';
import * as Ui from './styles';
import { TooltipInfo } from '../TooltipInfo';

function CardReferenciable(
  {
    /**
     * título da pergunta
     */
    questionTitle,
    /**
     * identificador do card
     */
    cardId,
    /**
     * título do card
     */
    cardTitle,
    /**
     * tooltip do card
     */
    cardTooltip,
    /**
     * subtítulo do card
     * formato de dados: { label:string, title:string, notFocusable: boolean }
     */
    cardTitleCompl,
    /**
     * define o campo que se refere ao tipo card
     * formato de dados: String
     */
    cardType,
    /**
     * define se está em modo de tela cheia
     */
    isFullScreenMode = false,
    /**
     * define se o card pode ser minimizado
     */
    minimizable = false,
    /**
     * define se deve apresentar um loading
     */
    loading = false,
    /**
     * define se o loading irá trabalhar em um overlay
     */
    withLoadingOverlay = false,
    /**
     * define se existe dados ou se apresenta uma tela configurada para vazio
     */
    withData = false,
    /**
     * verificação que retornará true ou false, irá definir se o empty card deverá ser renderizado de forma custom.
     * um exemplo de uso único até o momento é para o card do gráfico de evolutivo de cruzamentos.
     */
    customEmptyChartRules = false,
    /**
     * define a mensagem de vazio
     */
    emptyChartText = 'Crie uma configuração para visualizar seus resultados',
    /**
     * define os itens do menu
     * estrutura para o tipo de gráfico: {type='chart', onSelect, data = [{label, value}]}
     * estrutura para o tipo de abrir configuração: {type='config', onClick}
     */
    menu = [],
    /**
     * define um altura fixa para o corpo do card
     */
    contentHeight,
    /**
     * define se tem preset
     */
    withPresets = false,
    /**
     * habilita a ação de remoção do preset
     */
    withRemovablePreset = false,
    /**
     * habilita a ação de edição do preset
     */
    withEditablePreset = false,
    /**
     * define se o preset está carregando
     */
    loadingPresets = false,
    /**
     * array de presets
     */
    presets = [
      /* {label, value} */
    ],
    /**
     * propaga o evento de seleção de um preset
     */
    onSelectPreset,
    /**
     * propaga o evento de remoção de um preset
     */
    onRemovePreset,
    /**
     * propaga o evento de edição de um preset
     */
    onEditPreset,
    /**
     * define se os filtros de legenda não serão apresentados
     */
    withoutFilters = false,
    /**
     * define uma lista de tipos de filtragens
     */
    filtersTypes = [
      /* {value, label} */
    ],
    /**
     * propaga o evento de seleção de um tipo de filtro
     */
    onSelectFilterType,
    /**
     * propaga o evento de alteração do valor de filtragem do card
     */
    onChangeFilterValue,
    /**
     * propaga o evento de alteração do valor de filtragem por pergunta (feature de evolutivo de cruzamentos)
     */
    onChangeQuestionFilter,
    /**
     * propaga o evento de maximização do card
     */
    onMaximize,
    /**
     * propaga o evento de minimização do card
     */
    onMinimize,
    /**
     * define se o card tera scroll
     */
    withScroll = false,
    /**
     * define os itens selecionados
     */
    initialSelectedIndexes,
    /**
     * define se a pergunta é do tipo grade e desabilita a consolidação
     * para esse tipo de perguntas
     */
    isGradeQuestionType,
     /**
     * define se o card tera o menu de filter relacionado ao evolutivo de cruzamento
     */ 
    withQuestionFilter,
    /**
     * outros
     */
    ...props
  },
  ref,
) {
  // from parent or context
  const { children, theme, questionFilterData } = props;

  // refs
  const selectorRef = useRef();
  const inputSearchRef = useRef();
  const typedSearchRef = useRef();
  // state
  const [isFullScreen, setToggleFullScreen] = useState(isFullScreenMode);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [cardTitleComplToShow, setCardTitleComplToShow] = useState([]);
  // promp
  const [openConfirm, renderConfirm] = usePromptConfirm();

  // Garante que, ao setar o modo full screen em algum card de modo programático (sem interação com o usuário)
  // o estado do toggle será atualizado.
  useEffect(() => {
    if (isFullScreenMode !== isFullScreen) {
      setToggleFullScreen(isFullScreenMode);
    }
  }, [isFullScreenMode, isFullScreen]);
  /**
   * Callback executado para alteracoes em cardTitleCompl, fazendo
   * com que a formatacao para subtitulos com metadados aconteca
   */
  useEffect(() => {
    // effect condition
    if (!cardTitleCompl || cardTitleCompl.length === 0) {
      setCardTitleComplToShow([]);
      return;
    }
    // prepare title
    const separatorWord = ' / ';
    const totalOfSeparators = cardTitleCompl.length - 1;
    const newCardTitleComplToShow = [];
    cardTitleCompl.forEach((complement, index) => {
      newCardTitleComplToShow.push(complement);
      if (index < totalOfSeparators) {
        newCardTitleComplToShow.push({
          label: separatorWord,
          title: '',
          notFocusable: true,
        });
      }
    });
    // change title compl
    setCardTitleComplToShow(newCardTitleComplToShow);
  }, [cardTitleCompl]);

  /**
   * api para o card
   */
  useImperativeHandle(ref, () => ({
    clearPresetsSelector: () => {
      if (selectorRef.current) {
        selectorRef.current.clear();
      }
    },
    clearInputSearch: () => {
      if (inputSearchRef.current) {
        inputSearchRef.current.clear();
      }
    },
    clearTypedSearch: () => {
      if (typedSearchRef.current) {
        typedSearchRef.current.clear();
      }
    },
    clearAll: () => {
      if (selectorRef.current) {
        selectorRef.current.clear();
      }
      if (inputSearchRef.current) {
        inputSearchRef.current.clear();
      }
      if (typedSearchRef.current) {
        typedSearchRef.current.clear();
      }
    },
    selectPresetItemIndex: (index) => {
      if (selectorRef.current) {
        selectorRef.current.selectItemIndex(index);
      }
    },
    selectPresetItemValue: (value) => {
      if (selectorRef.current) {
        selectorRef.current.selectItemValue(value);
      }
    },
    refreshPresets: (keepSelectedValue) => {
      if (selectorRef.current) {
        selectorRef.current.refresh();
        if (keepSelectedValue) {
          selectorRef.current.selectItemValue(keepSelectedValue);
        }
      }
    },
  }));

  // toggle to full screen or not
  function handleToggleFullScreeen() {
    const currentIsFullScreen = !isFullScreen;
    setToggleFullScreen(currentIsFullScreen);
    if (onMaximize) {
      onMaximize(cardId, currentIsFullScreen);
    }
  }

  // handle on open setting screen event
  function handleOpenSetting(isOpen) {
    setIsSettingsOpen(isOpen);
  }

  function handleMinimizeScreeen() {
    if (onMinimize) onMinimize();
  }

  function handleSelectPreset(presetItem) {
    if (onSelectPreset) onSelectPreset(presetItem);
  }

  function handleRemovePreset(presetItem) {
    openConfirm(() => {
      if (onRemovePreset) onRemovePreset(presetItem);
    });
  }

  function handleEditPreset(presetItem) {
    if (onEditPreset) onEditPreset(presetItem);
  }

  function renderChildren() {
    return withData && !loading ? (
      children
    ) : (
      <EmptyChart 
        text={emptyChartText} 
        loading={loading} 
        theme={theme} 
        cardId={cardId}
        isCustomEmptyChart={customEmptyChartRules}
      />
    );
  }

  function renderChildrenWithOverlay() {
    return withData ? (
      <React.Fragment>{children}</React.Fragment>
    ) : (
      <EmptyChart text={emptyChartText} loading={loading} theme={theme} />
    );
  }

  return (
    <Ui.Container
      className="card"
      isFullScreen={isFullScreen}
      contentHeight={contentHeight || '100%'}
      withScroll={withScroll}
    >
      {cardType ? (
        <>
          <Ui.Header className="table-mode-header">
            <h2 className="card-title">
              {cardTitleCompl ? `${cardTitle}:` : cardTitle}
              {cardTitleCompl && (
                <p>
                  { theme.title === 'light' ? <ArrowRightDark /> : <ArrowRight />}
                  &nbsp;
                  {cardTitleComplToShow.map((titleCompl, index) => (
                    // eslint-disable-next-line max-len
                    <Ui.LabelSurveyQuestionComplement
                      key={`key-${index}`}
                      className={`card-title-complement ${
                        titleCompl.notFocusable
                          ? ''
                          : 'card-title-complement-focusable'
                      } `}
                      title={titleCompl.title}
                    >
                      {`${titleCompl.label}`}
                    </Ui.LabelSurveyQuestionComplement>
                  ))}
                </p>
              )}
            </h2>
            {!!questionTitle && (
              <Ui.LabelSurveyContent>
                <Ui.LabelSurveySurveyQuestionDescription>
                  {questionTitle}
                </Ui.LabelSurveySurveyQuestionDescription>
              </Ui.LabelSurveyContent>
            )}
            <Ui.HeaderActions>
              {/* action: minizable */}
              {minimizable && !isFullScreen ? (
                <IconButton
                  iconComponent={RetractIcon}
                  iconSize={20}
                  onClick={handleMinimizeScreeen}
                />
              ) : null}
              {/* action: full screen */}
              {!isFullScreen ? (
                <IconButton
                  iconComponent={ExpandIcon}
                  iconSize={20}
                  onClick={handleToggleFullScreeen}
                />
              ) : (
                <IconButton
                  iconComponent={RetractIcon}
                  iconSize={20}
                  onClick={handleToggleFullScreeen}
                />
              )}
              {/* configuration */}
              {menu && (
                <IconButton
                  iconComponent={MoreIcon}
                  iconSize={20}
                  onClick={() => handleOpenSetting(true)}
                />
              )}
              {/* action: open configuration */}
              {menu && isSettingsOpen && (
                <CardSettings
                  loading={loading || loadingPresets}
                  onClose={() => handleOpenSetting(false)}
                  itemsMenu={menu}
                  withData={withData}
                  withSettings={!!questionTitle && !customEmptyChartRules}
                />
              )}
            </Ui.HeaderActions>
          </Ui.Header>
          <Ui.FlexContainer>
            <Ui.HeaderGroup>
              {/* presets */}
              {withPresets && (
                <Ui.HeaderItem>
                  <Selector
                    dark
                    enableFilter
                    className="selector"
                    ref={selectorRef}
                    withRemovableItem={withRemovablePreset}
                    withEditableItem={withEditablePreset}
                    placeholder="Configurações"
                    inputSearchPlaceholder="Busque pelo nome da configuração"
                    isLoading={loadingPresets}
                    data={presets}
                    onChange={handleSelectPreset}
                    onTrashItem={handleRemovePreset}
                    onEditItem={handleEditPreset}
                    theme={theme}
                    initialSelectedIndexes={initialSelectedIndexes}
                  />
                </Ui.HeaderItem>
              )}
            </Ui.HeaderGroup>
            <Ui.CardType>{cardType}</Ui.CardType>
          </Ui.FlexContainer>
        </>
      ) : (
        <>
          <Ui.Header>
            <div className="card-title-container">
              <h2 className="card-title">
                {cardTitleCompl ? `${cardTitle}:` : cardTitle}
                {cardTooltip && <TooltipInfo toolTipText={cardTooltip} theme={theme} />}
              </h2>
              {!cardType ? <Ui.CardLine /> : ''}
            </div>
            <Ui.HeaderGroup className="justify-end">
              {/* presets */}
              {withPresets && (
                <Ui.HeaderItem>
                  <Selector
                    dark
                    enableFilter
                    className="selector"
                    ref={selectorRef}
                    withRemovableItem={withRemovablePreset}
                    withEditableItem={withEditablePreset}
                    placeholder="Configurações"
                    inputSearchPlaceholder="Busque pelo nome da configuração"
                    isLoading={loadingPresets}
                    data={presets}
                    onChange={handleSelectPreset}
                    onTrashItem={handleRemovePreset}
                    onEditItem={handleEditPreset}
                    theme={theme}
                    initialSelectedIndexes={initialSelectedIndexes}
                  />
                </Ui.HeaderItem>
              )}
            </Ui.HeaderGroup>
          </Ui.Header>
          <Ui.FlexContainer className="flex-container-has-border-bottom">
            {questionTitle && !withQuestionFilter ? (
              <Ui.LabelSurveyContent>
                <Ui.LabelSurveySurveyQuestionDescription>
                  {questionTitle}
                </Ui.LabelSurveySurveyQuestionDescription>
                {cardTitleCompl && (
                  <p>
                    { theme.title === 'light' ? <ArrowRightDark /> : <ArrowRight />}
                    &nbsp;
                    {cardTitleComplToShow.map((titleCompl, index) => (
                      // eslint-disable-next-line max-len
                      <Ui.LabelSurveyQuestionComplement
                        key={`key-${index}`}
                        className={`card-title-complement ${
                          titleCompl.notFocusable
                            ? ''
                            : 'card-title-complement-focusable'
                        } `}
                        title={titleCompl.title}
                      >
                        {`${titleCompl.label}`}
                      </Ui.LabelSurveyQuestionComplement>
                    ))}
                  </p>
                )}
              </Ui.LabelSurveyContent>
            ) : (
              questionTitle && withQuestionFilter 
                ? <GraphFilter 
                    mainQuestion={questionTitle}
                    filterData={questionFilterData}
                    onChange={onChangeQuestionFilter}
                    loading={loading}
                    hasConfigs={presets}
                  />
                : <div />
            )}
            <Ui.HeaderActions>
              {/* action: minizable */}
              {minimizable && !isFullScreen ? (
                <IconButton
                  iconComponent={RetractIcon}
                  iconSize={20}
                  onClick={handleMinimizeScreeen}
                />
              ) : null}
              {/* action: full screen */}
              {!isFullScreen ? (
                <IconButton
                  iconComponent={ExpandIcon}
                  iconSize={20}
                  onClick={handleToggleFullScreeen}
                />
              ) : (
                <IconButton
                  iconComponent={RetractIcon}
                  iconSize={20}
                  onClick={handleToggleFullScreeen}
                />
              )}
              {/* configuration */}
              {menu && (
                <IconButton
                  iconComponent={MoreIcon}
                  iconSize={20}
                  onClick={() => handleOpenSetting(true)}
                />
              )}
              {/* action: open configuration */}
              {menu && isSettingsOpen && (
                <CardSettings
                  loading={loading || loadingPresets}
                  onClose={() => handleOpenSetting(false)}
                  itemsMenu={menu}
                  withData={withData}
                  withSettings={!isGradeQuestionType && !customEmptyChartRules}
                />
              )}
            </Ui.HeaderActions>
          </Ui.FlexContainer>
        </>
      )}
      <div className="card-content">
        {withLoadingOverlay ? renderChildrenWithOverlay() : renderChildren()}
      </div>
      {/* prompt */}
      {renderConfirm(TrashIcon, 'Deseja realmente excluir este item')}
    </Ui.Container>
  );
}

const Card = React.forwardRef(CardReferenciable);
export default Card;
